import * as yup from 'yup';
import {
  Appointments,
  Businesses,
  Customers,
  Discounts,
  Orders,
  PaymentMethod,
  PaymentRefundReason,
  Payments,
  PaymentTransfers,
  SplitPaymentsGroups,
  Users,
} from '@prisma/client';
import { SORTABLE_KEYS } from '../../constants/payments';
import { Appointment, Customer } from '../../interfaces';
import { OrderWithItems } from '../orders';
import { SplitPaymentMethod } from '../../context/SplitPaymentsContext/context';
import { formatDollarAmount } from '../../utils/global/helper';
import { CheckoutPaymentMethod } from '../appointments';

export interface PaymentsSplit {
  id: string;
  type: 'tip' | 'payment';
  amount: number;
  transfersAmount: number;
}

export interface PaymentWithTransfers extends Payments {
  transfers?: PaymentTransfers[];
}

export interface PaymentWithTransfersOrders extends PaymentWithTransfers {
  order:
    | (OrderWithItems & {
        customer:
          | (Customer & {
              user: Users;
              internalNotes?: string;
            })
          | null;
      })
    | null;
}

export interface FullPayment extends PaymentWithTransfersOrders {
  customer:
    | (Customers & {
        user: Users;
        internalNotes?: string;
      })
    | null;
  appointment: Appointment | null;
}

export interface SplitPaymentsGroupWithCustomer extends SplitPaymentsGroups {
  customer: Customers;
}

export interface CreateSplitPaymentResponse extends InitiatePaymentResponse {
  payment: Payments;
}

export interface SplitPaymentsGroupWithPayments extends SplitPaymentsGroups {
  payments: Payments[];
}

export const getRefundPaymentSchema = (max: number) =>
  yup.object({
    amount: yup
      .number()
      .required('Amount is required')
      .min(0.01, 'Amount must be at least 0.01')
      .max(max, `Amount must be less than or equal to ${max}`),
    reason: yup
      .mixed<PaymentRefundReason>()
      .oneOf(Object.values(PaymentRefundReason)),
    otherReason: yup.string().when('reason', {
      is: PaymentRefundReason.OTHER,
      then: value => value.required('Reason is required'),
    }),
  });

export const orderItemSchema = yup.object({
  itemId: yup.string().nullable().optional(),
  price: yup
    .number()
    .min(0)
    .when('itemId', {
      is: Boolean,
      then: schema => schema.nullable().optional(),
      otherwise: schema => schema.required(),
    }),
  name: yup.string().when('itemId', {
    is: Boolean,
    then: schema => schema.nullable().optional(),
    otherwise: schema => schema.required(),
  }),
  quantity: yup.number().min(1).required(),
});

export const orderPaymentSchema = yup.object({
  orderItems: yup.array().of(orderItemSchema).required(),
  orderIdToDelete: yup.string().nullable().optional(),
  employeeId: yup.string().nullable().optional(),
});

export const appointmentPaymentSchema = yup.object({
  appointmentId: yup.string().required('Please select an appointment'),
  appointmentPrice: yup.number().min(0).required('Please enter a price'),
  isNoCharge: yup.boolean().default(false).nullable().optional(),
  includeBonus: yup.boolean().default(false).nullable().optional(),
});

export const initiatePaymentSchema = yup.object({
  paymentMethod: yup
    .mixed<PaymentMethod>()
    .oneOf(Object.values(PaymentMethod))
    .required('Payment method is required'),
  customerId: yup.string().required('Customer is invalid'),
  businessId: yup.string().required('Business is invalid'),
  tipAmount: yup.number().min(0).default(0).nullable().optional(),
  isManualCheckout: yup.boolean().default(false).nullable().optional(),
  isCashTaxed: yup.boolean().default(false).nullable().optional(),
  giftCardAmountCents: yup.number().integer().min(0).nullable().optional(),
  paymentIntentId: yup.string().nullable().optional(),
  order: orderPaymentSchema.nullable().optional(),
  appointment: appointmentPaymentSchema.nullable().optional(),
  discountId: yup.string().nullable().optional(),
});

export const createSplitPaymentsGroupSchema = yup.object({
  customerId: yup.string().required('Customer is invalid'),
  businessId: yup.string().required('Business is invalid'),
  order: orderPaymentSchema.nullable().optional(),
  appointment: appointmentPaymentSchema.nullable().optional(),
  discountId: yup.string().nullable().optional(),
});

export const createSplitPaymentSchema = yup.object({
  paymentMethod: yup
    .mixed<PaymentMethod>()
    .oneOf(Object.values(PaymentMethod))
    .required('Payment method is required'),
  amountCents: yup.number().integer().min(0).required('Amount is required'),
});

export type RefundPaymentPayload = yup.InferType<
  ReturnType<typeof getRefundPaymentSchema>
>;

export interface RefundPaymentResponse extends PaymentWithTransfers {}

export const REFUND_REASON_LABELS: Record<PaymentRefundReason, string> = {
  [PaymentRefundReason.DUPLICATE]: 'Duplicate',
  [PaymentRefundReason.REQUESTED_BY_CUSTOMER]: 'Requested by Customer',
  [PaymentRefundReason.OTHER]: 'Other',
};

export type InitiatePaymentPayload = yup.InferType<
  typeof initiatePaymentSchema
>;

export interface InitiatePaymentResponse {
  order: Orders;
  appointment: Appointments;
  paymentIntent:
    | {
        paymentId: string;
        id: string;
        client_secret: string;
      }
    | undefined;
  business: Businesses | null;
  customerId: string;
}

export type CreateSplitPaymentsGroupPayload = yup.InferType<
  typeof createSplitPaymentsGroupSchema
>;

export type CreateSplitPaymentPayload = yup.InferType<
  typeof createSplitPaymentSchema
>;

export interface CheckoutData {
  appointment: {
    originalPriceCents: number;
    discountAmountCents: number;
    discountedPriceCents: number;
    salesTaxCents: number;
    isDepositPaid: boolean;
    depositAmountPaidCents: number;
  };
  order: {
    originalPriceCents: number;
    discountAmountCents: number;
    discountedPriceCents: number;
    salesTaxCents: number;
  };
  totalBeforeTaxesAndFeesCents: number;
  totalOriginalPriceCents: number;
  discount: Discounts | null;
  discountAmountCents: number;
  feeCents: number;
  discountedPriceCents: number;
  salesTaxCents: number;
  giftCardAmountCents: number;
  totalPriceCents: number;
  totalPriceWithoutGiftCardCents: number;
  priceCents: number;
  isTotalCoveredByGiftCard: boolean;
  hasTaxRate: boolean;
}

export type PaymentsGroup = {
  groupKey: string;
  payments: (Payments & {
    transfers: PaymentTransfers[];
  })[];
  appointment: Appointments | null;
  order: OrderWithItems | null;
  customer:
    | (Customers & {
        user: Users;
      })
    | null;
};

export type PaymentsGroupResponse = {
  groups: PaymentsGroup[];
  totalCount: number;
};

export type SortableKeys = (typeof SORTABLE_KEYS)[number];

export type AddPaymentMethodPayload = {
  amount: number;
  paymentMethod: PaymentMethod;
};

export type SplitPaymentOnFinishArgs = {
  customerId: string | undefined | null;
  splitPaymentsGroupId?: string;
};

export const getSplitPaymentAmountSchema = (
  totalPriceCents: number,
  paymentMethods: SplitPaymentMethod[],
  currentIndex: number,
) => {
  const sum = paymentMethods
    .slice(0, currentIndex)
    .reduce((acc, curr) => acc + curr.amount, 0);

  const roundedMax = Math.round(totalPriceCents - sum * 100) / 100;
  const max = Math.max(0, roundedMax);
  return yup
    .number()
    .typeError('Enter a valid amount')
    .transform(val => (typeof val === 'string' ? parseFloat(val) : val))
    .min(
      0.01,
      `Payment amount must be at least ${formatDollarAmount(0.01, true)}`,
    )
    .max(
      max,
      `Payment amount must be less than or equal to ${formatDollarAmount(
        max,
        true,
      )}`,
    )
    .test('decimal-places', 'Enter a valid amount', val => {
      if (typeof val !== 'number') return false;
      const rounded = Math.round(val * 100) / 100;
      return val === rounded;
    })
    .required('Enter a valid amount');
};

const splitPaymentMethods = [
  'TERMINAL',
  'MANUAL',
  'CASH',
  'GIFT_CARD',
] as const satisfies CheckoutPaymentMethod[];

export const getSplitPaymentSchema = (
  totalPriceCents: number,
  paymentMethods: SplitPaymentMethod[],
  currentIndex: number,
) => {
  const numberSchema = getSplitPaymentAmountSchema(
    totalPriceCents,
    paymentMethods,
    currentIndex,
  );
  return yup.object({
    amount: numberSchema,
    paymentMethod: yup
      .mixed<(typeof splitPaymentMethods)[number]>()
      .oneOf(splitPaymentMethods)
      .required('Payment method is required'),
  });
};

export type SplitPaymentPayload = yup.InferType<
  ReturnType<typeof getSplitPaymentSchema>
>;

export type SplitPaymentForm = {
  amount: string;
  paymentMethod: CheckoutPaymentMethod;
};
